import { yupResolver } from '@hookform/resolvers/yup';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  CrmJobDTO,
  getAllLeadStatusByVisibility,
  getCrmJobById,
  getUserByEmail,
  IConvertToLeadDTO,
  LeadStatusDTO,
  ILeadOwnerInfo,
  getTaggedContacts,
  ClientContactDTO,
  convertCrmJobToLead,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  formatLocation,
  IUserDTO,
  Label,
  WsButton,
  WsColors,
  WsDrawer,
  WsSelect,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useUserContext } from '../../../context';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const validationSchema = object().shape({
  contactId: string().required('Please provide Contact'),
  leadStatusId: string().required('Please provide Lead Status'),
  leadOwnerId: string().required('Please provide Lead Owner'),
  description: string().optional(),
  leadOwnerInfo: object().nullable(),
});

interface ConvertToLeadDrawerProps {
  open: boolean;
  onClose: () => void;
  crmJobId: string | null;
}

export interface StatusList {
  label: string;
  value: string;
}

const ConvertToLeadDrawer = ({ open, onClose, crmJobId }: ConvertToLeadDrawerProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();
  const [crmJob, setCrmJob] = useState<CrmJobDTO | null>(null);
  const [statusList, updateStatusList] = useState<StatusList[]>([]);
  const [contactList, updateContactList] = useState<StatusList[]>([]);
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const methods = useForm<IConvertToLeadDTO>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      contactId: '',
      leadStatusId: '',
      leadOwnerId: '',
      description: '',
      leadOwnerInfo: null,
    },
  });

  const { handleSubmit, watch, setValue } = methods;
  const selectedEmail = watch('leadOwnerId') ?? '';

  useEffect(() => {
    if (loggedInUser?.email) {
      setValue('leadOwnerId', loggedInUser.email);
    }
  }, [loggedInUser?.email, setValue]);

  useEffect(() => {
    const fetchOwner = async () => {
      if (selectedEmail) {
        try {
          const response = await getUserByEmail(selectedEmail);
          const { firstName = '', lastName = '' } = response.data || {};
          const recruiterName = `${firstName} ${lastName}`.trim();
          const owner: ILeadOwnerInfo | null = response.data
            ? { name: recruiterName, id: selectedEmail }
            : null;
          setValue('leadOwnerId', selectedEmail);
          setValue('leadOwnerInfo', owner);
        } catch {
          setValue('leadOwnerInfo', null);
        }
      } else {
        setValue('leadOwnerInfo', null);
      }
    };
    fetchOwner();
  }, [selectedEmail, setValue]);

  useEffect(() => {
    const fetchCrmJob = async () => {
      const response = await getCrmJobById(crmJobId as string);
      response?.data && setCrmJob(response.data);
    };

    const getStatusList = async () => {
      const response = await getAllLeadStatusByVisibility();
      if (response?.data) {
        const statusData: StatusList[] = response.data.map((status: LeadStatusDTO) => ({
          label: status.leadStatusName || '',
          value: status.id,
        }));
        const defaultStatus =
          response.data.find((status: LeadStatusDTO) => status.makeDefaultStatus) ||
          response.data.find((status: LeadStatusDTO) => status.leadStatusName === 'Active');
        if (defaultStatus) {
          setValue('leadStatusId', defaultStatus.id);
        }
        updateStatusList(statusData);
      }
    };

    const getTaggedContactsDetails = async () => {
      const response = await getTaggedContacts(crmJobId as string);
      if (response?.data) {
        const contactData: StatusList[] = response.data.map((contact: ClientContactDTO) => ({
          label: contact.firstName + ' ' + contact.lastName,
          value: contact.id,
        }));
        updateContactList(contactData);
      }
    };

    if (crmJobId) {
      fetchCrmJob();
      getTaggedContactsDetails();
    }
    getStatusList();
  }, [crmJobId]);

  const onSubmit = async (data: IConvertToLeadDTO) => {
    data.clientId = crmJob?.client?.id ?? '';
    try {
      const response = await convertCrmJobToLead(data);
      if (response?.data) {
        setNotification(success('Conversion to Lead successful'));
        navigate(`/leads/details?leadId=${response.data.id}`);
      }
    } catch (error) {
      setNotification(failed('Error converting CRM job to lead'));
      console.error('Error converting CRM job to lead:', error);
    }
  };

  return (
    <WsDrawer open={open} onClose={onClose} size="medium" title="Convert to Lead">
      <Box p={3}>
        <Box mb={2} p={2} bgcolor={WsColors.BackGroundGrey} borderRadius={2}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>{crmJob?.title}</WsTypography>
          <Box display="flex" alignItems="center" gap={4} mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <CorporateFareIcon color="secondary" />
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
                {crmJob?.client?.name}
              </WsTypography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOnIcon color="secondary" />
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                {crmJob?.location ? formatLocation(crmJob.location) : 'Not Available'}
              </WsTypography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <WorkIcon color="secondary" />
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                {crmJob?.jobSource || 'Not Available'}
              </WsTypography>
            </Box>
          </Box>
        </Box>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          Add Details
        </WsTypography>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} display="flex" flexDirection="column" mt={1}>
              <Grid item xs={6}>
                <WsSelect
                  showMandatorySymbol
                  label="Select Contact"
                  name="contactId"
                  selectArray={contactList}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Label>Lead Status*</Label>
                <WsSelect
                  showMandatorySymbol
                  label=""
                  name="leadStatusId"
                  selectArray={statusList}
                  size="small"
                  disabled
                />
              </Grid>

              <Grid item xs={6}>
                <Label>Lead Owner*</Label>
                <ControlledTextInput fullWidth name="leadOwnerId" label="" disabled={true} />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextArea name="description" labelText="Comments" minRows={5} />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <WsButton variant="outlined" onClick={onClose}>
                Cancel
              </WsButton>
              <WsButton variant="contained" type="submit">
                Convert Lead
              </WsButton>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </WsDrawer>
  );
};

export default ConvertToLeadDrawer;
